import { useHistory, useParams, useLocation } from "react-router-dom";
import { Button } from "../../components/button.component";
import { AppRouteUi } from "../../config/appRoute";
import searchIcon from "../../assets/img/search-icon.svg";
import { StorageAuth } from "../../core/localStorage/StorageAuth";
import { useEffect, useState } from "react";
import { ApiAppointment } from "../../core/apis/appointment";
import { ApiSchemaAppointment } from "../../models/api";
import { UtilityDate } from "../../utils/dateUtils";
import { AUPatientTable } from "./patientTable";
import { addHours } from "date-fns/esm";

type TableData = ApiSchemaAppointment & {
  name?: string;
  image?: string;
  taskDNames?: string;
  conflict?: boolean;
};

export const AddPatientOnCaregiver = () => {
  const { caregiverId } = useParams<{ caregiverId: string }>();
  const routeHistory = useHistory();
  const { state } = useLocation<{ companyId?: string }>();
  const companyId = StorageAuth.CompanyId || state.companyId || "";

  const [isAppointmentLoading, setIsAppointmentLoading] = useState(true);
  const [assigned, setAssigned] = useState<TableData[]>([]);
  const [unassigned, setUnassigned] = useState<TableData[]>([]);
  const [preAssigned, setPreAssigned] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>("");

  const generateTableData = (res: ApiSchemaAppointment[]): TableData[] => {
    return res.map((appointment) => ({
      ...appointment,
      name:
        appointment.patient?.firstName + " " + appointment.patient?.lastName,
      image: appointment.patient?.profilePic,
    }));
  };

  // function that checks if two date range overlap
  const isSameDate = (
    startDate: Date,
    endDate: Date,
    startDate2: Date,
    endDate2: Date
  ): boolean => {
    return (
      (startDate <= endDate2 && endDate >= startDate2) ||
      (startDate2 <= endDate && endDate2 >= startDate)
    );
  };

  // function to check if two time ranges overlap ignoring date
  const isTimeInBetween = (
    startTime: Date,
    endTime: Date,
    startTime2: Date,
    endTime2: Date
  ): boolean => {
    const sameDayEndTime = UtilityDate.combineDateTime(startTime, endTime);
    const sameDayStartTime2 = UtilityDate.combineDateTime(
      startTime,
      startTime2
    );
    const sameDayEndTime2 = UtilityDate.combineDateTime(startTime, endTime2);
    return (
      (startTime <= sameDayEndTime2 && sameDayEndTime >= sameDayStartTime2) ||
      (sameDayStartTime2 <= sameDayEndTime && sameDayEndTime2 >= startTime)
    );
  };

  useEffect(() => {
    setAssigned(
      assigned.map(
        (appointment: TableData, index: Number, assigned: TableData[]) => {
          const startTime = UtilityDate.combineDateTime(
            new Date(appointment.appointmentStartDate),
            new Date(appointment.taskStartTime)
          );
          const endTime = UtilityDate.combineDateTime(
            new Date(appointment.appointmentEndDate),
            addHours(
              new Date(appointment.taskStartTime),
              Number(appointment.maxHours)
            )
          );

          const conflict = assigned.some((item: TableData, index2: Number) => {
            if (index === index2) return false;
            const startTime2 = UtilityDate.combineDateTime(
              new Date(item.appointmentStartDate),
              new Date(item.taskStartTime)
            );
            const endTime2 = UtilityDate.combineDateTime(
              new Date(item.appointmentEndDate),
              addHours(new Date(item.taskStartTime), Number(item.maxHours))
            );
            return (
              isSameDate(startTime, endTime, startTime2, endTime2) &&
              isTimeInBetween(startTime, endTime, startTime2, endTime2)
            );
          });

          return { ...appointment, conflict };
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassigned]);

  useEffect(() => {
    ApiAppointment.GetAppointmentAssignments(caregiverId, companyId).then(
      (res) => {
        setAssigned(generateTableData(res?.assigned ?? []));
        setPreAssigned(
          res?.assigned ? res.assigned?.map((item) => item.sKey) : []
        );
        setUnassigned(generateTableData(res?.unassigned ?? []));
        setIsAppointmentLoading(false);
      }
    );
  }, [caregiverId, companyId]);

  const addToAssigned = (row: TableData) => {
    setUnassigned(
      unassigned.filter((appointment) => appointment.sKey !== row.sKey)
    );
    if (!assigned.find((appointment) => appointment.sKey === row.sKey)) {
      setAssigned([...assigned, row]);
    }
  };

  const removeFromAssigned = (row: TableData) => {
    setAssigned(
      assigned.filter((appointment) => appointment.sKey !== row.sKey)
    );
    if (!unassigned.find((appointment) => appointment.sKey === row.sKey)) {
      setUnassigned([...unassigned, row]);
    }
  };

  const handleSaved = async () => {
    setIsLoading(true);
    const added = assigned
      .filter((item) => !preAssigned.includes(item.sKey))
      .map((item) => item.sKey);
    const removed = preAssigned.filter(
      (sKey) => !assigned.map((item) => item.sKey).includes(sKey)
    );
    await ApiAppointment.ManageCaregiverAssignments({
      caregiverId,
      added,
      removed,
    });
    routeHistory.length > 1
      ? routeHistory.goBack()
      : routeHistory.push(AppRouteUi.Root);
    setIsLoading(false);
  };

  return (
    <div className="p-2">
      <div className="d-flex justify-content-start border-bottom pb-4 mb-3 border-green-200">
        <Button
          className="m-1 px-5 btn-green"
          type="submit"
          name="Save"
          onClick={handleSaved}
          loading={isLoading}
          disabled={assigned.some((item) => item.conflict)}
        />
        <Button
          onClick={() => {
            routeHistory.length > 1
              ? routeHistory.goBack()
              : routeHistory.push(AppRouteUi.Root);
          }}
          type="button"
          className="m-1 btn-green-subtle"
          name="Return without saving"
        />
      </div>
      <div className="row">
        <div className="col-md-6 col-lg-6 col-12 mb-3 p-3">
          <form className="search-component mb-3">
            <div className="input-group me-3">
              <span
                className="input-group-text pe-2"
                id="search-component-word"
              >
                <img src={searchIcon} alt="search-icon" />
              </span>
              <input
                type="text"
                className="form-control ps-0"
                placeholder="Search..."
                aria-label="Search"
                aria-describedby="search-component-word"
                onChange={(e) => setSearchKey(e.target.value)}
                value={searchKey}
              />
            </div>
          </form>
          {/* <p className="text-green-200 py-3">
            <i className="far fa-question-circle me-2" />
            Click on column(s) to add in the list
          </p> */}
          <AUPatientTable
            patientData={unassigned}
            isAppointmentLoading={isAppointmentLoading}
            isLoading={isLoading}
            addToAssigned={addToAssigned}
            removeFromAssigned={removeFromAssigned}
            searchKey={searchKey}
            isAssignedTable={false}
          />
        </div>

        <div className="col-md-6 p-3">
          <div className="d-flex justify-content-center text-muted align-items-center text-center border-bottom my-2">
            <h4 className="fw-light">Assigned Patient(s)</h4>
          </div>
          <AUPatientTable
            patientData={assigned}
            isAppointmentLoading={isAppointmentLoading}
            isLoading={isLoading}
            addToAssigned={addToAssigned}
            removeFromAssigned={removeFromAssigned}
            searchKey={searchKey}
            isAssignedTable={true}
          />
        </div>
      </div>
    </div>
  );
};
